import { useEffect, useState } from 'react'

import { useGAUnlock } from '../../../../NewGAPurchaseWrapper/GAUnlockProvider/GAUnlockProvider'

import {
  formatOfferListPrice,
  formatCurrency,
  capitalizeString,
  getValidQuantitiesForTicketGroup,
} from '../../../../../../utilities/helpers'

import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'
import Collapse from 'react-bootstrap/Collapse'

import { QuickpickBadge } from '../../../../NewSeatedPurchaseWrapper/NewSeatedPurchaseContainer/TicketSelectionPanel/ListingsContainer/TicketGroup/TicketGroup'
import DetailsButton from '../../../DetailsButton/DetailsButton'
import { AddSubtractQuantity } from '../../../AddSubtractQuantity'
import { Spinner } from '../../../../../LoadingContainer/Spinner'
import JoinWaitlist from '../../../JoinWaitlist/JoinWaitlist'

import './GATickets.scss'

function resolveOfferState(quickpick, { isScheduled } = {}) {
  const available = Number(quickpick?.availableCount ?? 0)
  if (available <= 0) {
    return isScheduled ? 'scheduled' : 'soldout'
  }
  if (isScheduled) return 'scheduled'
  return 'live'
}

function StatusPill({ state }) {
  const meta =
    state === 'live'
      ? { label: 'On sale', cls: '--live' }
      : state === 'scheduled'
        ? { label: 'Scheduled', cls: '--scheduled' }
        : { label: 'Sold out', cls: '--soldout' }

  return (
    <span className={`ga-status-pill ${meta.cls}`}>
      <span className="ga-status-pill__dot" aria-hidden />
      {meta.label}
    </span>
  )
}

export default function GATickets({
  quickpicks,
  selectQuickPick,
  checkingAvailability,
  event,
  isScheduled = false,
  scheduledTime = null,
}) {
  const { openUnlockModal } = useGAUnlock()
  const [openIndex, setOpenIndex] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [reminded, setReminded] = useState({})

  const getPriceForConnectedOffer = (PLName, am_pricing_objects) => {
    const correctPricingObject = am_pricing_objects.find(
      (PO) => PO.name === PLName
    )
    return correctPricingObject.totalDue
  }

  let allTickets = quickpicks.reduce((acc, quickpick) => {
    if (!quickpick.GA) {
      return acc
    }

    acc.push({
      type: 'main',
      quickpick,
      isLocked: quickpick.offer.accessCode && !quickpick?.offer?.unlocked,
      state: resolveOfferState(quickpick, { isScheduled }),
    })

    if (quickpick.offer?.connected_offers?.length) {
      quickpick.offer.connected_offers.forEach((discountOffer) => {
        const connectedQuickpick = {
          ...quickpick,
          price: getPriceForConnectedOffer(
            quickpick.PLName,
            discountOffer.am_pricing_objects
          ),
          offer: {
            ...discountOffer,
          },
        }
        acc.push({
          type: 'connected-offer',
          quickpick: connectedQuickpick,
          state: resolveOfferState(connectedQuickpick, { isScheduled }),
        })
      })
    }

    return acc
  }, [])

  allTickets = allTickets.sort((a, b) => {
    if (a?.quickpick?.price < b?.quickpick?.price) return -1
    if (a?.quickpick?.price > b?.quickpick?.price) return 1
    return 0
  })

  return (
    <ul className="vjx-ga-ul">
      {allTickets?.map((ticket, index) => {
        if (ticket.isLocked) {
          return (
            <LockedTicket
              key={`ticket-${index}`}
              index={index}
              open={openIndex === index}
              quickpick={ticket.quickpick}
              openUnlockModal={openUnlockModal}
            />
          )
        }

        return (
          <GATicket
            key={`ticket-${index}`}
            index={index}
            open={openIndex === index}
            setOpen={() => {
              if (ticket.state === 'live') setOpenIndex(index)
            }}
            quantity={quantity}
            setQuantity={setQuantity}
            quickpick={ticket.quickpick}
            selectQuickPick={selectQuickPick}
            checkingAvailability={checkingAvailability}
            eventTicketLimit={event?.globalTicketLimit || event?.ticketLimit}
            eventUuid={event?.uuid}
            state={ticket.state}
            scheduledTime={scheduledTime}
            reminded={!!reminded[index]}
            onRemind={() => setReminded((prev) => ({ ...prev, [index]: true }))}
          />
        )
      })}
    </ul>
  )
}

function LockedTicket({ index, open, quickpick, openUnlockModal }) {
  const offer = quickpick.offer
  return (
    <li
      aria-controls={`ticket-collapse-text-${index}`}
      aria-expanded={open}
      className={`vjx-ga-ticket ${open ? '--active' : ''}`}
    >
      <Stack direction="horizontal" className="justify-content-between">
        <div className="vjx-locked-left-col">
          <span className="vjx-ticket-type">{offer?.name}</span>
          <QuickpickBadge
            isListing={false}
            listing={undefined}
            inventoryType={offer?.inventoryType}
            offerName={'Locked Offer'}
          />
        </div>
        <Button
          variant="primary"
          size="lg"
          className="d-flex justify-content-center m-0 vjx-checkout-btn"
          onClick={() => openUnlockModal(offer)}
        >
          Unlock
        </Button>
      </Stack>
    </li>
  )
}

function GATicket({
  index,
  open,
  setOpen,
  quantity,
  setQuantity,
  quickpick,
  selectQuickPick,
  checkingAvailability,
  eventTicketLimit,
  state = 'live',
  scheduledTime,
  reminded,
  onRemind,
  eventUuid,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const offer = quickpick.offer
  const validQuantities = getValidQuantitiesForTicketGroup(
    offer,
    quickpick.availableCount,
    eventTicketLimit
  )
  const maxTickets = validQuantities[validQuantities.length - 1]
  const unitPriceLabel = formatOfferListPrice(quickpick?.price, offer)
  const checkoutTotal =
    offer?.freeOffer || !quickpick?.price
      ? unitPriceLabel
      : formatCurrency(Number(quickpick.price) * Number(quantity || 1))

  useEffect(() => {
    if (!open) {
      setDetailsOpen(false)
    }
  }, [open])

  useEffect(() => {
    if (
      open &&
      validQuantities.length > 0 &&
      !validQuantities.includes(quantity)
    ) {
      setQuantity(validQuantities[0])
    }
  }, [open, validQuantities, quantity, setQuantity])

  const handleDetailsButtonClick = (e) => {
    e.stopPropagation()
    setDetailsOpen(!detailsOpen)
  }

  const increment = (e) => {
    e?.stopPropagation?.()
    if (validQuantities.length === 0) return
    const newQuantity = validQuantities.find((q) => q > quantity) || null
    if (newQuantity !== null) setQuantity(newQuantity)
  }

  const decrement = (e) => {
    e?.stopPropagation?.()
    if (validQuantities.length === 0 || quantity === 0) return
    if (quantity === validQuantities[0]) {
      setQuantity(validQuantities[0])
      return
    }
    const newQuantity = validQuantities.filter((q) => q < quantity).pop() || 0
    if (newQuantity !== null) setQuantity(newQuantity)
  }

  const note =
    state === 'live'
      ? maxTickets
        ? `Ticket limit: ${maxTickets} per order`
        : 'Incl. taxes and fees'
      : state === 'scheduled'
        ? scheduledTime
          ? `On sale ${scheduledTime}`
          : 'Coming soon'
        : offer?.description || 'Currently unavailable'

  const sub =
    offer?.description ||
    (quickpick?.sectionName
      ? capitalizeString(quickpick.sectionName)
      : 'General admission')

  return (
    <li
      onClick={setOpen}
      aria-controls={`ticket-collapse-text-${index}`}
      aria-expanded={open}
      className={`vjx-ga-ticket ${open && state === 'live' ? '--active' : ''} ${
        state === 'soldout' ? '--soldout' : ''
      }`}
    >
      <div className="vjx-ga-txt-col">
        <div className="d-flex d-flex-column">
          <span className="vjx-ticket-type">{offer?.name}</span>
          <StatusPill state={state} />
          <span className="vjx-ticket-sub">{sub}</span>
        </div>
        <div className="d-flex d-flex-column align-items-end">
          <span className="vjx-ticket-price">{unitPriceLabel}</span>
          <span className="vjx-ticket-taxes">
            {offer?.freeOffer ? 'Complimentary' : 'Incl. taxes and fees'}
          </span>
        </div>
      </div>

      {state === 'live' ? (
        <Collapse in={open}>
          <div id={`ticket-collapse-text-${index}`}>
            <div className="vjx-collapse-one">
              {offer?.description ? (
                <DetailsButton
                  text={'Ticket details'}
                  onClick={handleDetailsButtonClick}
                  aria-controls={`ticket-details-text-${index}`}
                  aria-expanded={detailsOpen}
                />
              ) : (
                <span className="caption caption-bold text-muted">{note}</span>
              )}
              <div className="vjx-quantity-button">
                <AddSubtractQuantity
                  whiteBG={true}
                  quantity={quantity}
                  increment={increment}
                  decrement={decrement}
                />
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    selectQuickPick(quickpick, quantity)
                  }}
                  disabled={checkingAvailability || quantity < 1}
                  variant="primary"
                  size="lg"
                  className="d-flex justify-content-center m-0 vjx-checkout-btn"
                >
                  {checkingAvailability ? (
                    <Spinner />
                  ) : (
                    `Checkout ${checkoutTotal}`
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Collapse>
      ) : (
        <div className="vjx-collapse-one">
          <span className="caption caption-bold text-muted">{note}</span>
          {state === 'soldout' ? (
            <JoinWaitlist
              eventUuid={eventUuid}
              offerId={offer?.id}
              offerUuid={offer?.uuid}
              offerName={offer?.name}
            />
          ) : (
            <button
              type="button"
              className={`ga-secondary-btn ${reminded ? '--done' : ''}`}
              onClick={(e) => {
                e.stopPropagation()
                onRemind?.()
              }}
            >
              {reminded ? 'Reminder set' : 'Remind me'}
            </button>
          )}
        </div>
      )}

      <Collapse in={detailsOpen}>
        <div id={`ticket-details-text-${index}`}>
          <div className="vjx-collapse-two">
            <pre>{offer?.description}</pre>
          </div>
        </div>
      </Collapse>
    </li>
  )
}
